<script setup lang="ts">
import { computed } from 'vue';
import { useTranslation } from 'i18next-vue';
import { Tab, TabList, Tabs } from 'primevue';

import { CloseTinyIcon, UiButton } from '@peanuds/ui';
import type { NotificationsTab } from '../types/notifications.types';

interface Props {
  tab: NotificationsTab;
  unreadCount: number;
}
const { tab, unreadCount } = defineProps<Props>();

interface Emits {
  close: [];
  'update:tab': [value: NotificationsTab];
}

defineEmits<Emits>();

const { t } = useTranslation();

const badgeFontSizeClass = computed(() => {
  const digitCount = unreadCount.toString().length;

  if (digitCount === 2) {
    return 'notifications-drawer-header__title-badge--two-digits';
  }

  if (digitCount >= 3) {
    return 'notifications-drawer-header__title-badge--three-digits';
  }

  return null;
});
</script>

<template>
  <div class="notifications-drawer-header">
    <div class="notifications-drawer-header__header">
      <div class="notifications-drawer-header__title-group">
        <h2 class="notifications-drawer-header__title">{{ t('nav.notification_center') }}</h2>
        <div
          v-if="unreadCount > 0"
          :class="['notifications-drawer-header__title-badge', badgeFontSizeClass]"
          aria-label="Unread notification count"
        >
          {{ unreadCount }}
        </div>
      </div>

      <UiButton
        iconOnly
        severity="secondary"
        variant="ghost"
        aria-label="Close notifications"
        class="notifications-drawer-header__close"
        @click="$emit('close')"
      >
        <template #icon>
          <CloseTinyIcon />
        </template>
      </UiButton>
    </div>

    <Tabs :value="tab" @update:value="$emit('update:tab', $event as NotificationsTab)">
      <TabList class="notifications-drawer-header__tabs">
        <Tab value="all">{{ t('nav.notifications_tabs.all') }}</Tab>
        <Tab value="account">{{ t('nav.notifications_tabs.account') }}</Tab>
        <Tab value="payments">{{ t('nav.notifications_tabs.payments') }}</Tab>
      </TabList>
    </Tabs>
  </div>
</template>

<style scoped lang="scss">
.notifications-drawer-header {
  background: var(--p-surface-0);
  /* Figma: 0px 8px 13.8px rgba(0,0,0,0.07) */
  /* eslint-disable-next-line declaration-property-value-disallowed-list */
  box-shadow: 0 0.5rem 0.8625rem 0 rgba(0, 0, 0, 0.07);
  padding-top: $static-space-3;
  padding-left: $static-space-4;
  padding-right: $static-space-4;
}

.notifications-drawer-header__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $space-md;
  padding: $static-space-3;
}

.notifications-drawer-header__title {
  margin: 0;
  font-size: $text-xl;
  font-weight: $font-weight-semibold;
  line-height: 1.3;
  color: var(--p-surface-700);
}

.notifications-drawer-header__title-group {
  display: inline-flex;
  align-items: center;
  gap: $static-space-2;
}

.notifications-drawer-header__title-badge {
  box-sizing: border-box;
  min-width: 1.65rem;
  height: 1.65rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 2px solid var(--p-surface-0);
  background: var(--p-primary-500);
  color: var(--p-surface-0);
  font-size: $text-sm;
  font-weight: $font-weight-semibold;
  line-height: 1;

  &--two-digits {
    font-size: $text-xs;
  }

  &--three-digits {
    font-size: 10px;
  }
}

.notifications-drawer-header__close {
  width: $icon-lg;
  height: $icon-lg;
  padding: 0;
  color: var(--p-surface-500);
}

.notifications-drawer-header__tabs {
  --p-tabs-tablist-background: var(--p-surface-0);
  --p-tabs-tablist-border-color: var(--p-surface-200);
  --p-tabs-tab-background: transparent;
  --p-tabs-tab-active-background: transparent;
  --p-tabs-tab-border-color: var(--p-surface-200);
  --p-tabs-tab-active-border-color: var(--p-primary-500);
  --p-tabs-tab-color: var(--p-surface-500);
  --p-tabs-tab-active-color: var(--p-primary-500);
  --p-tabs-tab-gap: 0.4375rem;
  --p-tabs-tab-font-weight: $font-weight-semibold;
  --p-tabs-active-bar-height: 1px;
  --p-tabs-active-bar-background: var(--p-primary-500);
  --p-tabs-tab-padding: 0.875rem 0.984375rem 0.9375rem;

  :deep(.p-tab) {
    font-size: $text-sm;
  }
}
</style>
